import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ShoppingBag, Loader2, MapPin } from "lucide-react";
import { useT, useLang } from "@/context/LangContext";
import { localizeProduct } from "@/lib/product";
import { displayPrice, LANG_CURRENCY } from "@/lib/currency";
import { ProductGallery } from "@/components/ProductGallery";

export default function Products() {
  const t = useT();
  const { lang, currency } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [category, setCategory] = useState("__all__");

  useEffect(() => {
    api.get("/products").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const s = new Set(items.map((p) => p.category).filter(Boolean));
    return Array.from(s);
  }, [items]);

  const filtered = category === "__all__" ? items : items.filter((p) => p.category === category);

  const buy = async (id) => {
    setPaying(id);
    try {
      const { data } = await api.post("/checkout/session", {
        product_id: id, quantity: 1, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch { setPaying(null); alert(t("products.err")); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="products-page">
      <p className="kicker">{t("products.kicker")}</p>
      <h1 className="text-5xl lg:text-7xl font-display font-black tracking-tighter mt-4 max-w-4xl">
        {t("products.title1")} <span className="text-[var(--accent)]">{t("products.title2")}</span>
      </h1>
      <p className="text-[var(--ink-soft)] mt-6 max-w-2xl text-lg">{t("products.desc")}</p>

      {/* Category filter */}
      {!loading && categories.length > 0 && (
        <div className="flex gap-2 flex-wrap mt-10" data-testid="cat-filters">
          <button
            onClick={() => setCategory("__all__")}
            data-testid="cat-all"
            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all ${category === "__all__" ? "bg-[var(--ink)] text-white" : "bg-[var(--bg-soft)] text-[var(--ink)] hover:bg-black/5"}`}
          >
            {t("filter.all")} · {items.length}
          </button>
          {categories.map((c) => {
            const count = items.filter((p) => p.category === c).length;
            const active = category === c;
            return (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all ${active ? "bg-[var(--accent)] text-white" : "bg-[var(--bg-soft)] text-[var(--ink)] hover:bg-black/5"}`}
              >
                {c} · {count}
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <p className="mt-20 text-center text-[var(--ink-soft)]">{t("products.empty")}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-10">
          {filtered.map((raw) => {
            const p = localizeProduct(raw, lang);
            const priceInfo = displayPrice(Number(p.price), p.currency || "EUR", lang, currency);
            const showBase = priceInfo.currency !== (p.currency || "EUR");
            return (
              <div key={p.id} className="card-flat overflow-hidden flex flex-col group" data-testid={`product-${p.id}`}>
                {(p.images?.length > 0 || p.image_url) && (
                  <div className="aspect-[4/3] overflow-hidden bg-[var(--bg-soft)] relative">
                    <ProductGallery images={p.images?.length ? p.images : [p.image_url]} alt={p.name} />
                    <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-10 pointer-events-none">
                      {p.stock > 0 && (
                        <span className="text-[10px] font-bold uppercase tracking-widest bg-green-500 text-white px-2.5 py-1 rounded-full flex items-center gap-1.5 w-fit">
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                          {t(p.stock < 10 ? "stock.low" : "stock.in")}
                        </span>
                      )}
                      {p.in_stock_senegal && (
                        <span className="text-[10px] font-bold uppercase tracking-widest bg-[var(--accent)] text-white px-2.5 py-1 rounded-full flex items-center gap-1 w-fit" data-testid={`badge-senegal-${p.id}`}>
                          <MapPin size={10} /> {t("stock.senegal")}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  <p className="kicker !text-[var(--secondary)]">{p.category}</p>
                  <h3 className="font-display text-xl font-bold mt-2">{p.name}</h3>
                  <p className="text-[var(--ink-soft)] text-sm mt-3 flex-1">{p.description}</p>
                  <div className="flex items-center justify-between mt-6 pt-4 border-t border-black/5">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-[var(--ink-soft)]">{t("products.from")}</p>
                      <p className="font-display text-2xl font-black" data-testid={`price-${p.id}`}>{priceInfo.text}</p>
                      {showBase && (
                        <p className="text-[10px] text-[var(--ink-soft)] uppercase tracking-widest mt-0.5">≈ {priceInfo.base}</p>
                      )}
                    </div>
                    <button onClick={() => buy(p.id)} disabled={paying === p.id} className="btn-primary !py-2 !px-4 text-sm" data-testid={`buy-${p.id}`}>
                      {paying === p.id ? <Loader2 className="animate-spin" size={16} /> : <><ShoppingBag size={14} /> {t("products.buy")}</>}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-xs text-[var(--ink-soft)] mt-8 opacity-70">
        {currency} · {t("products.rate")}
      </p>
    </div>
  );
}
